import { InstancedMesh, Mesh, Quaternion, Ray, TransformNode, Vector3 } from '@babylonjs/core';
import { BabylonjsModule } from '@modules/babylonjs/src/BabylonjsModule';
import { BaseSystem, SystemOperationDefinition, Zed } from '@modules/core';
import { clearArchetype, createEntityFactory, GLTFLoader, SourceParameter, ZEDCamera } from '@modules/standard';
import { World } from 'miniplex';

import { Danji2DFacilityExtendData } from '../../APIResponse';
import * as TreeModels from '../../assets/model/tree';
import { MaterialManager, MaterialType } from '../../graphic/material/MaterialManager';
import { TourDanjiFetcher } from '../../TourDanjiFetcher';
import { TOURDANJI_MODULE_OPTIONS_TOKEN, TourDanjiOptions } from '../../TourDanjiModule';
import { FacilityType, MeshType, TreeType } from '../component/data';
import { Entity } from '../Entity';

import { RaycastSystem } from './RaycastSystem';
import { LayerMask } from '../../Constants';

// TODO: 공통 나무 모델을 사용하는 것으로 변경되었기 때문에 성능 개선의 여지가 있음
const treeModelUrls = {
  [TreeType.Normal]: {
    A: TreeModels.TreeNormalA_LOD3,
    B: TreeModels.TreeNormalB_LOD3,
    C: TreeModels.TreeNormalC_LOD3,
    D: TreeModels.TreeNormalD_LOD3,
  },
  [TreeType.Flower]: {
    A: TreeModels.TreeFlowerA_LOD3,
    B: TreeModels.TreeFlowerB_LOD3,
    C: TreeModels.TreeFlowerC_LOD3,
    D: TreeModels.TreeFlowerD_LOD3,
  },
  [TreeType.Bush]: {
    A: TreeModels.TreeBushA_LOD3,
    C: TreeModels.TreeBushC_LOD3,
  },
};

/**
 * 설명 추가 필요
 *
 * @group System
 * @category System
 */
export class TreeSystem implements BaseSystem {
  static readonly INJECTABLE_TOKEN = Symbol.for('TreeSystem');

  //------------------------------------------------------------------------------------
  // #### System Base ####
  //------------------------------------------------------------------------------------
  private readonly danjiOptions;

  private readonly world;
  private readonly scene;
  private readonly danjiFetcher;
  private readonly gltfLoader;
  private readonly raycastSystem;
  private readonly materialManager;

  private readonly createEntity;

  private readonly treeArchetype;

  private readonly groupSize = 1;

  private treeAssets: Map<TreeType, Mesh[]> = new Map();
  private treeOriginalScale: Map<number, Vector3> = new Map();
  private treeGroup: Map<
    string,
    {
      flavorIndex: number;
      position: Vector3;
      rotationQuaternion: Quaternion;
      scale: Vector3;
    }[]
  > = new Map();

  private flowerGroup: Map<
    string,
    {
      flavorIndex: number;
      position: Vector3;
      rotationQuaternion: Quaternion;
      scale: Vector3;
    }[]
  > = new Map();

  private bushGroup: Map<
    string,
    {
      flavorIndex: number;
      position: Vector3;
      rotationQuaternion: Quaternion;
      scale: Vector3;
    }[]
  > = new Map();

  private instancedMeshes: Map<string, InstancedMesh[]> = new Map();

  //-----------------------
  constructor({
    [TOURDANJI_MODULE_OPTIONS_TOKEN]: danjiOptions,

    [Zed.WORLD_TOKEN]: world,
    [BabylonjsModule.INJECTABLE_TOKEN]: babylonjs,
    [TourDanjiFetcher.INJECTABLE_TOKEN]: danjiFetcher,
    [GLTFLoader.INJECTABLE_TOKEN]: gltfLoader,
    [RaycastSystem.INJECTABLE_TOKEN]: raycastSystem,
    [MaterialManager.INJECTABLE_TOKEN]: materialManager,
  }: {
    [TOURDANJI_MODULE_OPTIONS_TOKEN]: TourDanjiOptions;

    [Zed.WORLD_TOKEN]: World<Entity>;
    [BabylonjsModule.INJECTABLE_TOKEN]: Pick<BabylonjsModule, 'scene'>;
    [TourDanjiFetcher.INJECTABLE_TOKEN]: TourDanjiFetcher;
    [GLTFLoader.INJECTABLE_TOKEN]: GLTFLoader;
    [RaycastSystem.INJECTABLE_TOKEN]: RaycastSystem;
    [MaterialManager.INJECTABLE_TOKEN]: MaterialManager;
  }) {
    this.danjiOptions = danjiOptions;

    this.world = world;
    this.scene = babylonjs.scene;

    this.danjiFetcher = danjiFetcher;
    this.gltfLoader = gltfLoader;
    this.raycastSystem = raycastSystem;
    this.materialManager = materialManager;

    this.createEntity = createEntityFactory(this.world);

    this.treeArchetype = world.with('DanjiMeshes', 'Tree');
  }

  dispose() {
    this.instancedMeshes.forEach((ms) => ms.forEach((m) => m.dispose(false, true)));
    this.instancedMeshes.clear();
    this.treeAssets.forEach((ms) => ms.forEach((m) => m.dispose(false, true)));
    this.treeAssets.clear();
    this.treeOriginalScale.clear();
    this.treeGroup.clear();
    clearArchetype(this.treeArchetype);
  }

  // --------------------------------
  private enableCulling: boolean = false;
  public setEnableCulling(enable: boolean) {
    this.enableCulling = enable;
    if (enable) {
      for (const entity of this.treeArchetype.entities) {
        for (const dm of entity.DanjiMeshes.danjiMeshes) {
          for (const mesh of dm.meshes) {
            if (!mesh.isEnabled()) mesh.setEnabled(true);
          }
        }
      }
    }
  }

  // --------------------------------
  private caculateRay?: Ray;
  private readonly maxDistance = 850;
  private readonly oneQueueMaxCount = 200;
  private currentQueue = 0;
  /** @deprecated */
  private beforeRenderExecute = () => {
    if (!this.enableCulling) return;

    // const s = Date.now();

    const length = this.treeArchetype.entities.length;
    const divideQueue = length > this.oneQueueMaxCount ? Math.floor(length / this.oneQueueMaxCount) : 1;
    const step = this.oneQueueMaxCount;
    const remain = length - this.oneQueueMaxCount * (divideQueue - 1);
    const totalCount = step * (divideQueue - 1) + remain;

    let finishFrame = this.currentQueue === divideQueue - 1;
    const start = step * this.currentQueue;
    const end = finishFrame ? totalCount : step * (this.currentQueue + 1);

    const camera = this.scene.activeCamera;
    if (camera && camera instanceof ZEDCamera) {
      const cameraPosition = camera.globalPosition.clone().add(Vector3.Up().scale(10));

      for (let i = start; i < end; i++) {
        const entity = this.treeArchetype.entities[i]!;
        const entityId = entity.entityId;
        const center = entity.Tree.center;
        const distance = Vector3.Distance(cameraPosition, center) - 10;
        const direction = center.subtract(cameraPosition).normalize();
        if (!this.caculateRay) {
          this.caculateRay = new Ray(cameraPosition, direction, distance);
        } else {
          this.caculateRay.origin = cameraPosition;
          this.caculateRay.direction = direction;
          this.caculateRay.length = distance;
        }

        let visible = distance < this.maxDistance;
        if (visible) visible = this.raycastSystem.isOccludedWithRayByOctree(entityId, this.caculateRay);
        for (const dm of entity.DanjiMeshes.danjiMeshes) {
          for (const mesh of dm.meshes) {
            if (mesh.isEnabled() !== visible) mesh.setEnabled(visible);
          }
        }
      }
    }

    // const e = Date.now();
    // console.log(length, totalCount, this.currentQueue, divideQueue, step, remain, start, end, e - s);

    this.currentQueue++;
    if (this.currentQueue === divideQueue) {
      this.currentQueue = 0;
    }
  };

  // --------------------------------
  public async preloadTreeAssets(parentTransform: TransformNode) {
    try {
      const treeModels = Object.keys(TreeType).flatMap((type) => {
        const value = TreeType[type as TreeType];
        return Object.entries(treeModelUrls[value]).flatMap(([flavor, src]) => {
          const assetName = `Tree_${TreeType[value]}_${flavor}_LOD${3}`;
          return {
            src,
            modelName: assetName,
            parentTransform: parentTransform,
            origibalScaling: true,
          } as SourceParameter;
        });
      });

      const result = await this.gltfLoader.loadModels(this.scene, treeModels, undefined, {
        autoLoadTexture: true,
        autoCompileShader: true,
      });

      const set = (type: TreeType, mesh: Mesh) => {
        if (!this.treeAssets.has(type)) {
          this.treeAssets.set(type, []);
        }
        this.treeAssets.get(type)!.push(mesh);
        this.treeOriginalScale.set(mesh.uniqueId, mesh.scaling.clone());
      };

      result.forEach((r) => {
        if (r.root && r.root instanceof Mesh) {
          r.root.material?.dispose();
          r.root.useVertexColors = false;
          if (r.root.name.includes(TreeType.Normal))
            if (r.root.name.includes('_A_'))
              r.root.material = this.materialManager.createMaterial(MaterialType.Tree_Leave_Normal_SpringA);
            else if (r.root.name.includes('_B_'))
              r.root.material = this.materialManager.createMaterial(MaterialType.Tree_Leave_Normal_SpringB);
            else if (r.root.name.includes('_C_'))
              r.root.material = this.materialManager.createMaterial(MaterialType.Tree_Leave_Normal_SpringC);
            else if (r.root.name.includes('_D_'))
              r.root.material = this.materialManager.createMaterial(MaterialType.Tree_Leave_Normal_SpringD);
            else r.root.material = this.materialManager.createMaterial(MaterialType.Tree_Leave_Normal_SpringA);
          else if (r.root.name.includes(TreeType.Bush))
            r.root.material = this.materialManager.createMaterial(MaterialType.Tree_Leave_Bush_Spring);
          else if (r.root.name.includes(TreeType.Flower))
            if (r.root.name.includes('_A_'))
              r.root.material = this.materialManager.createMaterial(MaterialType.Tree_Leave_Flower_SpringA);
            else if (r.root.name.includes('_B_'))
              r.root.material = this.materialManager.createMaterial(MaterialType.Tree_Leave_Flower_SpringB);
            else if (r.root.name.includes('_C_'))
              r.root.material = this.materialManager.createMaterial(MaterialType.Tree_Leave_Flower_SpringC);
            else if (r.root.name.includes('_D_'))
              r.root.material = this.materialManager.createMaterial(MaterialType.Tree_Leave_Flower_SpringD);
            else r.root.material = this.materialManager.createMaterial(MaterialType.Tree_Leave_Flower_SpringA);
          

          if (r.root.name.includes(TreeType.Normal)) set(TreeType.Normal, r.root);
          else if (r.root.name.includes(TreeType.Bush)) set(TreeType.Bush, r.root);
          else if (r.root.name.includes(TreeType.Flower)) set(TreeType.Flower, r.root);
          r.root.position = Vector3.Zero();
          r.root.rotation = Vector3.Zero();
          r.root.isVisible = false;
        }
      });
    } catch (ex) {
      console.warn(`Failed to fetch the tree meshes with error: ${ex}`);
    }
  }
  public async generateTrees() {
    if (!this.danjiFetcher.raw2dFacilityData) return;

    const normalTreeMeshes = this.treeAssets.get(TreeType.Normal);
    if (normalTreeMeshes && normalTreeMeshes.length > 0) {
      const treeName = '나무';
      const treeData = this.danjiFetcher.facility2DDatas?.filter((d) => d.name === treeName);
      this.generateTree(normalTreeMeshes, treeData);

      await Object.values(normalTreeMeshes).map((m) => {
        return m.material?.forceCompilationAsync(m);
      });
    }

    const bushMeshes = this.treeAssets.get(TreeType.Bush);
    if (bushMeshes && bushMeshes.length > 0) {
      const bushName = '수풀';
      const bushData = this.danjiFetcher.facility2DDatas?.filter((d) => d.name === bushName);
      this.generateBush(bushMeshes, bushData);

      await Object.values(bushMeshes).map((m) => {
        return m.material?.forceCompilationAsync(m);
      });
    }

    const flowerTreeMeshes = this.treeAssets.get(TreeType.Flower);
    if (flowerTreeMeshes && flowerTreeMeshes.length > 0) {
      const floatTreeName = '꽃나무';
      const floorData = this.danjiFetcher.facility2DDatas?.filter((d) => d.name === floatTreeName);
      this.generateFlower(flowerTreeMeshes, floorData);

      await Object.values(flowerTreeMeshes).map((m) => {
        return m.material?.forceCompilationAsync(m);
      });
    }

    this.instancedMeshes.forEach((meshes) => {
      let center = Vector3.Zero();
      meshes.forEach((mesh) => {
        mesh.isVisible = true;
        mesh.isPickable = false;
        mesh.layerMask = LayerMask.Facility;
        center.addInPlace(mesh.position);
      });
      center.divideInPlace(Vector3.One().scale(meshes.length));

      this.createEntity<Omit<Entity, 'entityId'>>({
        ...{
          Facility: {
            type: FacilityType.Tree,
            facilityName: 'Tree',
            extends: { min: Vector3.One(), max: Vector3.One() },
          },
          DanjiMeshes: {
            danjiMeshes: [{ type: MeshType.FACILITY_TREE_NORMAL, meshes: meshes }],
          },
          Tree: {
            center: center,
          },
        },
      });
    });
  }

  private generateTree(treeMeshes: Mesh[], dataList: Danji2DFacilityExtendData[] | undefined) {
    if (!treeMeshes || !dataList || dataList.length === 0) {
      return;
    }

    dataList.forEach((data) => {
      data.positions.forEach((position, i) => {
        const flavors = Object.keys(treeModelUrls[TreeType.Normal]);
        const flavorIndex = Math.floor(Math.random() * flavors.length);
        const groupingKey = `Tree_Normal_${flavors[flavorIndex]}_${Math.floor(
          position.x / this.groupSize
        )}_${Math.floor(position.z / this.groupSize)}`;

        if (!this.treeGroup.has(groupingKey)) this.treeGroup.set(groupingKey, []);

        const rotationQuaternion = data.rotations.at(i) ?? Quaternion.Identity();
        const originalScale = this.treeOriginalScale.get(treeMeshes[flavorIndex]!.uniqueId)!.clone();

        const scaleData = data.scaleInPlaces.at(i) ?? 1;
        const scale = originalScale.add(Vector3.One().scale(scaleData));

        this.treeGroup.get(groupingKey)!.push({
          flavorIndex: flavorIndex,
          position: position,
          rotationQuaternion: rotationQuaternion,
          scale: scale,
        });
      });

      this.treeGroup.forEach((group, groupKey) => {
        group.forEach((value) => {
          if (!this.instancedMeshes.has(groupKey)) {
            this.instancedMeshes.set(groupKey, []);
          }
          const baseMesh = treeMeshes[value.flavorIndex]!;
          const instancedMesh = baseMesh.createInstance(groupKey);
          instancedMesh.parent = baseMesh.parent;
          instancedMesh.position = value.position;
          instancedMesh.rotationQuaternion = value.rotationQuaternion;
          instancedMesh.scaling = value.scale;
          instancedMesh.useVertexColors = false;
          instancedMesh.occlusionRetryCount = 0;
          instancedMesh.occlusionType = Mesh.OCCLUSION_TYPE_OPTIMISTIC;

          this.instancedMeshes.get(groupKey)!.push(instancedMesh);
        });
      });
    });
  }

  private generateFlower(treeMeshes: Mesh[], dataList: Danji2DFacilityExtendData[] | undefined) {
    if (!treeMeshes || !dataList || dataList.length === 0) {
      return;
    }

    dataList.forEach((data) => {
      data.positions.forEach((position, i) => {
        const flavors = Object.keys(treeModelUrls[TreeType.Flower]);
        const flavorIndex = Math.floor(Math.random() * flavors.length);
        const groupingKey = `Tree_Flower_${flavors[flavorIndex]}_${Math.floor(
          position.x / this.groupSize
        )}_${Math.floor(position.z / this.groupSize)}`;

        if (!this.flowerGroup.has(groupingKey)) this.flowerGroup.set(groupingKey, []);

        const rotationQuaternion = data.rotations.at(i) ?? Quaternion.Identity();
        const originalScale = this.treeOriginalScale.get(treeMeshes[flavorIndex]!.uniqueId)!.clone();

        const scaleData = data.scaleInPlaces.at(i) ?? 1;
        const scale = originalScale.add(Vector3.One().scale(scaleData));

        this.flowerGroup.get(groupingKey)!.push({
          flavorIndex: flavorIndex,
          position: position,
          rotationQuaternion: rotationQuaternion,
          scale: scale,
        });
      });

      this.flowerGroup.forEach((group, groupKey) => {
        group.forEach((value) => {
          if (!this.instancedMeshes.has(groupKey)) {
            this.instancedMeshes.set(groupKey, []);
          }
          const baseMesh = treeMeshes[value.flavorIndex]!;
          const instancedMesh = baseMesh.createInstance(groupKey);
          instancedMesh.parent = baseMesh.parent;
          instancedMesh.position = value.position;
          instancedMesh.rotationQuaternion = value.rotationQuaternion;
          instancedMesh.scaling = value.scale;
          instancedMesh.useVertexColors = false;
          instancedMesh.occlusionRetryCount = 0;
          instancedMesh.occlusionType = Mesh.OCCLUSION_TYPE_OPTIMISTIC;

          this.instancedMeshes.get(groupKey)!.push(instancedMesh);
        });
      });
    });
  }

  private generateBush(treeMeshes: Mesh[], dataList: Danji2DFacilityExtendData[] | undefined) {
    if (!treeMeshes || !dataList || dataList.length === 0) {
      return;
    }

    dataList.forEach((data) => {
      data.positions.forEach((position, i) => {
        const flavors = Object.keys(treeModelUrls[TreeType.Bush]);
        const flavorIndex = Math.floor(Math.random() * flavors.length);
        const groupingKey = `Tree_Bush_${flavors[flavorIndex]}_${Math.floor(position.x / this.groupSize)}_${Math.floor(
          position.z / this.groupSize
        )}`;

        if (!this.bushGroup.has(groupingKey)) this.bushGroup.set(groupingKey, []);

        const rotationQuaternion = data.rotations.at(i) ?? Quaternion.Identity();
        const originalScale = this.treeOriginalScale.get(treeMeshes[flavorIndex]!.uniqueId)!.clone();

        const scaleData = data.scaleInPlaces.at(i) ?? 1;
        const scale = originalScale.add(Vector3.One().scale(scaleData));

        this.bushGroup.get(groupingKey)!.push({
          flavorIndex: flavorIndex,
          position: position,
          rotationQuaternion: rotationQuaternion,
          scale: scale,
        });
      });

      this.bushGroup.forEach((group, groupKey) => {
        group.forEach((value) => {
          if (!this.instancedMeshes.has(groupKey)) {
            this.instancedMeshes.set(groupKey, []);
          }
          const baseMesh = treeMeshes[value.flavorIndex]!;
          const instancedMesh = baseMesh.createInstance(groupKey);
          instancedMesh.parent = baseMesh.parent;
          instancedMesh.position = value.position;
          instancedMesh.rotationQuaternion = value.rotationQuaternion;
          instancedMesh.scaling = value.scale;
          instancedMesh.useVertexColors = false;
          instancedMesh.occlusionRetryCount = 0;
          instancedMesh.occlusionType = Mesh.OCCLUSION_TYPE_OPTIMISTIC;

          this.instancedMeshes.get(groupKey)!.push(instancedMesh);
        });
      });
    });
  }

  readonly operations: SystemOperationDefinition[] = [
    // {
    //   group: 'beforeRender',
    //   run: this.beforeRenderExecute,
    // },
  ];
}
